import React, { useState } from "react";
import { ConsensusEventMessage } from "../ExplorerTestPage";
import { EventDetailsModal } from "./EventDetailsModal";
import {
  ConsensusEventFlat,
  TransactionEvent,
} from "../../sdk/types/api";

interface ConsensusEventListProps {
  events: ConsensusEventMessage[];
}

// Helper function to extract hash from event
const getEventHash = (event: ConsensusEventFlat | TransactionEvent): string => {
  // Check if this is a TransactionEvent (from transactions channel)
  if ("data" in event && event.data && typeof event.data === "object") {
    const data = event.data as any;
    if ("hash" in data && data.hash) {
      return data.hash;
    }
    if ("txhash" in data && data.txhash) {
      return data.txhash;
    }
    if ("id" in data && data.id) {
      return data.id;
    }
  }

  // Check if this is a ConsensusEventFlat (from consensus_flat channel)
  if ("data" in event && event.data && typeof event.data === "object") {
    const data = event.data as any;
    if ("id" in data && data.id) {
      return data.id;
    }
    if ("txhash" in data && data.txhash) {
      return data.txhash;
    }
  }

  return "N/A";
};

export const ConsensusEventList: React.FC<ConsensusEventListProps> = ({
  events,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<ConsensusEventMessage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleEventClick = (event: ConsensusEventMessage) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  if (events.length === 0) {
    return (
      <div className="consensus-event-list empty">
        <p>No events received yet. Connect and subscribe to start receiving consensus events.</p>
      </div>
    );
  }

  return (
    <>
      <div className="consensus-event-list">
        <div className="event-list-header">
          <h3>Consensus Events ({events.length})</h3>
        </div>
        <div className="event-list-table">
          <div className="event-list-table-header">
            <div className="event-list-table-cell hash-column">Hash</div>
            <div className="event-list-table-cell time-column">Time</div>
            <div className="event-list-table-cell type-column">Type</div>
          </div>
          <div className="event-list-table-body">
            {events.map((event) => {
              const hash = getEventHash(event.event);
              const time = new Date(event.timestamp).toLocaleString();
              const eventType = event.event.event_type || "unknown";

              return (
                <div
                  key={event.id}
                  className="event-list-table-row"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="event-list-table-cell hash-column" title={hash}>
                    {hash.length > 20 ? `${hash.substring(0, 20)}...` : hash}
                  </div>
                  <div className="event-list-table-cell time-column">
                    {time}
                  </div>
                  <div className="event-list-table-cell type-column">
                    <span className={`event-type-badge ${event.type}`}>
                      {eventType}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <EventDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        event={selectedEvent}
      />
    </>
  );
};

